import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAppContext } from '../context/AppContext'; // 导入 AppContext 提供器

const MAX_LOGS = 500; // 最多显示 500 行日志
const RECONNECT_INTERVAL = 3000; // 3 秒后重连
const MAX_RECONNECT_ATTEMPTS = 10; // 最大重连次数

//////////////////////////////////////////////////////////////////////////////
//                                  业务区                                   //
//////////////////////////////////////////////////////////////////////////////
function convertToWebSocketURL(url) {
    try {
        let parsedUrl = new URL(url);
        let wsProtocol = parsedUrl.protocol === "https:" ? "wss:" : "ws:"; // 替换协议头
        return `${wsProtocol}//${parsedUrl.host}${parsedUrl.pathname.replace(/\/$/, "")}`; // 确保结尾没有 /
    } catch (error) {
        console.error("❌ 无效的 URL:", url);
        return null; // 返回 null 以防止错误
    }
}

const LogMonitor = () => {
    const { ServiceURLPrefix, logWSRetries } = useAppContext();
    const [logs, setLogs] = useState([]); // 存储日志
    const [isPaused, setIsPaused] = useState(false); // 是否暂停日志更新
    const logsEndRef = useRef(null); // 滚动到底部用的 ref
    const wsRef = useRef(null); // 存储 WebSocket 连接
    const reconnectTimeoutRef = useRef(null); // 追踪重连计时器

    // 🔹 滚动到底部
    const scrollToBottom = useCallback(() => {
        if (logsEndRef.current && !isPaused) {
            const logContainer = logsEndRef.current.parentElement;
            // 确保日志容器的高度和滚动高度
            //const containerHeight = logContainer.clientHeight;
            const scrollHeight = logContainer.scrollHeight;
            //const scrollTop = logContainer.scrollTop;

            // 如果当前滚动位置接近底部，则滚动到底部
            //if (scrollHeight - scrollTop <= containerHeight + 100) {
                logContainer.scrollTop = scrollHeight;
            //}
        }
    }, [isPaused]);

    // 🔹 处理新日志（避免不必要的 UI 更新）
    const handleNewLog = useCallback((message) => {
        setLogs((prevLogs) => [...prevLogs, message].slice(-MAX_LOGS));
    }, []);

    // 🔹 连接 WebSocket 并添加自动重连
    const connectWebSocket = useCallback(() => {
        if (wsRef.current) {
            console.warn("⚠️ 旧 WebSocket 仍然存活，先关闭...");
        wsRef.current.onclose = null;
            wsRef.current.close();
        wsRef.current = null;
        }

        const wssurl_prefix = convertToWebSocketURL(ServiceURLPrefix);
        if (!wssurl_prefix) return;

        wsRef.current = new WebSocket(`${wssurl_prefix}/logstream`);

        // 心跳机制
        const heartbeatInterval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send("ping");
            }
        }, 30000); // 每30秒发送一次心跳

        wsRef.current.onopen = () => {
            console.log("✅ WebSocket 连接成功！");
        logWSRetries.current = 0;
            handleNewLog("INFO ✅ WebSocket 连接成功！");
        };

        wsRef.current.onerror = (error) => {
            console.error("❌ WebSocket 连接错误:", error);
            handleNewLog("ERROR ❌ WebSocket 连接错误: " + error);
        clearInterval(heartbeatInterval);
        };

        wsRef.current.onclose = () => {
            console.warn("⚠️ WebSocket 连接已关闭");
            handleNewLog("WARNING ⚠️ WebSocket 连接已关闭");
        clearInterval(heartbeatInterval);

            if (logWSRetries.current < MAX_RECONNECT_ATTEMPTS) {
                logWSRetries.current += 1;
                console.log(`🔄 尝试重新连接 (${logWSRetries.current}/${MAX_RECONNECT_ATTEMPTS})...`);
                handleNewLog(`INFO 🔄 尝试重新连接 (${logWSRetries.current}/${MAX_RECONNECT_ATTEMPTS})...`);

            // 指数退避重连
            const delay = Math.min(RECONNECT_INTERVAL * Math.pow(2, logWSRetries.current), 30000);
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
            } else {
                console.warn("🚨 已达到最大重连次数，停止重连");
                handleNewLog("WARNING 🚨 已达到最大重连次数，停止重连");
            }
        };

        wsRef.current.onmessage = (event) => {
            //if (!isPaused && event.data !== "pong") { // 忽略心跳响应
            if ( event.data !== "pong") { // 忽略心跳响应
                handleNewLog(event.data);
            }
        };

    return () => clearInterval(heartbeatInterval);
    //}, [ServiceURLPrefix, handleNewLog, isPaused, logWSRetries]);
    }, [ServiceURLPrefix, handleNewLog, logWSRetries]);

    // 🔹 初始化 WebSocket 连接
    useEffect(() => {
        connectWebSocket();
        return () => {
            if (wsRef.current) {
                console.log("🛑 组件卸载，关闭 WebSocket");
                wsRef.current.onclose = null;
                wsRef.current.close();
                wsRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [connectWebSocket]);


    // 观察 logWSRetries.current 从非零变成 0 的变化，一旦检测到这个变化，就调用 connectWebSocket() 重新连接
    const prevLogWSRetriesRef = useRef(logWSRetries.current);
    useEffect(() => {
        const interval = setInterval(() => {
            const prev = prevLogWSRetriesRef.current;
            const current = logWSRetries.current;
            if (prev !== 0 && current === 0) {
                console.log("[LogMonitor] logWSRetries reset to 0, reconnecting WebSocket...");
                connectWebSocket();
            }
            prevLogWSRetriesRef.current = current;
        }, 3000); // 每秒检测一次
    
        return () => clearInterval(interval);
    }, [connectWebSocket, logWSRetries]);
    

    // 🔹 监听 `logs` 变化并滚动到底部（避免 `isPaused` 时滚动）
    useEffect(() => {
        scrollToBottom();
    }, [logs, scrollToBottom]);

    return (
        <div style={{ ...styles.container, position: 'relative', overflow: 'hidden' }}>
            <div style={styles.logContainer} contenteditable="false">
                {logs.map((log, index) => (
                    <div key={index} style={getLogStyle(log)}>
                        {log}
                    </div>
                ))}
                <div ref={logsEndRef}></div>
            </div>
            <button onClick={() => setIsPaused(!isPaused)} style={styles.button}>
                {isPaused ? "恢复日志滚动" : "暂停日志滚动"}
            </button>
        </div>
    );
};

// 🔹 设置日志颜色（按级别区分）
const getLogStyle = (log) => {
    let style = { whiteSpace: "pre-wrap", userSelect: "text"}; // ✨ 加这个！
    if (log.includes("EXCEPTION")) Object.assign(style, { color: "red", fontWeight: "bold", fontStyle: "italic" });
    else if (log.includes("ASSERT")) Object.assign(style, { color: "red", fontStyle: "italic" });
    else if (log.includes("CRITICAL")) Object.assign(style, { color: "red", fontWeight: "bold" });
    else if (log.includes("ERROR")) Object.assign(style, { color: "red" });
    else if (log.includes("WARNING")) Object.assign(style, { color: "orange" });
    else if (log.includes("INFO")) Object.assign(style, { color: "green" });
    else if (log.includes("DEBUG")) Object.assign(style, { color: "gray" });
    else Object.assign(style, { color: "white" });
    return style;
};

// 🔹 样式
const styles = {
    container: {
        width: "98%",
        margin: "5px auto",
        backgroundColor: "#222",
        color: "#fff",
        padding: "4px",
        borderRadius: "2px",
        fontFamily: "monospace",
    },
    logContainer: {
        height: "300px",
        overflowY: "auto",
        backgroundColor: "#111",
        padding: "4px",
        borderRadius: "2px",
        border: "1px solid #444",
        fontSize: "10px",
        overscrollBehavior: "none",
    },
    button: {
        display: "block",
        margin: "1px auto",
        padding: "1px 3px",
        backgroundColor: "#007BFF",
        color: "#fff",
        border: "none",
        borderRadius: "2px",
        cursor: "pointer",
        fontSize: "9px",
    },   
};

export default LogMonitor;
